import React, { useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import {
  IconUser,
  IconLock,
  IconEye,
  IconEyeOff,
  IconAlertCircle,
  IconLoader2,
  IconArrowRight,
  IconX,
  IconShield,
  IconMail,
  IconKey
} from '@tabler/icons-react';
import LogoIcon from '../components/LogoIcon';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';

const AuthPage = ({ mode = 'login' }) => {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showCaptchaModal, setShowCaptchaModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [loginVerifyEmail, setLoginVerifyEmail] = useState(null);
  const [verifyCode, setVerifyCode] = useState(['', '', '', '', '', '']);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const codeRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const captchaRef = useRef(null);
  
  const { login, register, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const redirectUrl = searchParams.get('redirect');

  // Обработка успешной капчи - сразу отправляем форму
  const handleCaptchaVerify = async (token) => {
    setCaptchaToken(token);
    setShowCaptchaModal(false);
    
    // Автоматически отправляем форму после капчи
    setLoading(true);
    try {
      await register(username, email, password, token);
      captchaRef.current?.reset();
      setCaptchaToken(null);
      // Показываем экран подтверждения почты
      setRegistrationEmail(email);
    } catch (err) {
      setError(err.message || 'Произошла ошибка');
    } finally {
      setLoading(false);
      setPendingSubmit(false);
    }
  };

  // Закрытие модального окна
  const handleCloseModal = () => {
    setShowCaptchaModal(false);
    setPendingSubmit(false);
    setError('Пройдите проверку hCaptcha для регистрации');
  };

  // Повторная отправка письма с подтверждением email
  const handleResend = async () => {
    setResending(true);
    setResendMessage(null);
    try {
      await authAPI.resendVerification(registrationEmail || email);
      setResendMessage('Письмо отправлено. Проверьте почту.');
    } catch (err) {
      setResendMessage(err.message || 'Не удалось отправить письмо');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResendMessage(null);
    
    if (!username.trim() || !password.trim()) {
      setError('Заполните все поля');
      return;
    }

    if (!isLogin && !email.trim()) {
      setError('Укажите email');
      return;
    }

    if (!isLogin && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Некорректный email');
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    // Валидация пароля только при регистрации
    if (!isLogin) {
      if (password.length < 8) {
        setError('Пароль должен быть минимум 8 символов');
        return;
      }
      if (!/[A-Z]/.test(password)) {
        setError('Пароль должен содержать минимум одну заглавную букву');
        return;
      }
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        setError('Пароль должен содержать минимум один специальный символ (!@#$%^&*...)');
        return;
      }
      // Если капча не пройдена - показываем модальное окно
      if (!captchaToken) {
        setPendingSubmit(true);
        setShowCaptchaModal(true);
        return;
      }
    }

    setLoading(true);
    
    try {
      if (isLogin) {
        await login(username, password);
        // Редирект на redirect URL или dashboard
        const targetUrl = redirectUrl || '/dashboard';
        navigate(targetUrl);
      } else {
        await register(username, email, password, captchaToken);
        captchaRef.current?.reset();
        setCaptchaToken(null);
        // Регистрация требует подтверждения email
        setRegistrationEmail(email);
      }
    } catch (err) {
      const code = err.response?.data?.code;
      setError(err.message || 'Произошла ошибка');
      if (code === 'email_not_verified') {
        setLoginVerifyEmail(err.response?.data?.email || username);
      }
    } finally {
      setLoading(false);
    }
  };

  // Повторная отправка письма с подтверждением email (при попытке входа)
  const handleResendVerification = async () => {
    setResending(true);
    setResendMessage(null);
    try {
      await authAPI.resendVerification(loginVerifyEmail);
      setResendMessage('Письмо отправлено. Проверьте почту.');
    } catch (err) {
      setResendMessage(err.message || 'Не удалось отправить письмо');
    } finally {
      setResending(false);
    }
  };

  // Экран ввода кода после регистрации
  if (registrationEmail) {

    const handleCodeChange = (index, value) => {
      if (!/^\d*$/.test(value)) return;
      const newCode = [...verifyCode];
      newCode[index] = value.slice(-1);
      setVerifyCode(newCode);
      if (value && index < 5) {
        codeRefs[index + 1].current?.focus();
      }
    };

    const handleCodeKeyDown = (index, e) => {
      if (e.key === 'Backspace' && !verifyCode[index] && index > 0) {
        codeRefs[index - 1].current?.focus();
      }
    };

    const handleCodePaste = (e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
      if (pasted) {
        const newCode = pasted.split('').concat(Array(6 - pasted.length).fill(''));
        setVerifyCode(newCode.slice(0, 6));
        const focusIdx = Math.min(pasted.length, 5);
        codeRefs[focusIdx].current?.focus();
      }
    };

    const handleVerifyCode = async () => {
      const fullCode = verifyCode.join('');
      if (fullCode.length !== 6) {
        setVerifyError('Введите 6-значный код');
        return;
      }
      setVerifyLoading(true);
      setVerifyError(null);
      try {
        const data = await authAPI.verifyEmailByCode(fullCode);
        if (data && data.access_token) {
          localStorage.setItem('access_token', data.access_token);
        }
        if (data && data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
        await refreshUser();
        navigate('/dashboard');
      } catch (err) {
        setVerifyError(err.message || 'Неверный код');
      } finally {
        setVerifyLoading(false);
      }
    };

    return (
      <div className="min-h-screen bg-white dark:bg-dark-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2.5">
              <LogoIcon />
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                XN Connect
              </span>
            </Link>
          </div>

          <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 sm:p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Подтверждение email
              </h2>
              <p className="text-gray-600 dark:text-dark-400 text-sm leading-relaxed">
                Код отправлен на{' '}
                <span className="font-medium text-gray-900 dark:text-white">{registrationEmail}</span>.
                Введите 6-значный код из письма или перейдите по ссылке в письме.
              </p>
            </div>

            {verifyError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
                <IconAlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{verifyError}</p>
              </div>
            )}

            {resendMessage && (
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
                <IconMail className="w-5 h-5 text-green-400 flex-shrink-0" />
                <p className="text-green-400 text-sm">{resendMessage}</p>
              </div>
            )}

            <div className="flex justify-center gap-2 sm:gap-3 mb-6">
              {verifyCode.map((digit, i) => (
                <input
                  key={i}
                  ref={codeRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  onPaste={handleCodePaste}
                  className="w-11 h-13 sm:w-13 sm:h-14 text-center text-xl font-bold bg-white dark:bg-dark-950 border-2 border-primary-500 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-primary-400 transition-colors"
                  disabled={verifyLoading}
                />
              ))}
            </div>

            <button
              onClick={handleVerifyCode}
              disabled={verifyLoading || verifyCode.join('').length !== 6}
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white flex items-center justify-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-3"
            >
              {verifyLoading ? (
                <IconLoader2 className="w-5 h-5 animate-spin" />
              ) : (
                <span>Подтвердить</span>
              )}
            </button>

            <button
              onClick={handleResend}
              disabled={resending}
              className="w-full py-2.5 border border-gray-300 dark:border-dark-800 rounded-lg font-medium text-gray-700 dark:text-dark-300 hover:bg-gray-50 dark:hover:bg-dark-950 transition-colors disabled:opacity-50"
            >
              {resending ? (
                <IconLoader2 className="w-4 h-4 animate-spin inline mr-2" />
              ) : null}
              Отправить письмо ещё раз
            </button>

            <button
              onClick={() => { setRegistrationEmail(null); setVerifyCode(['', '', '', '', '', '']); setVerifyError(null); setResendMessage(null); }}
              className="w-full mt-3 py-2.5 text-sm text-gray-500 dark:text-dark-500 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              ← Назад к регистрации
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-dark-950 flex items-center justify-center p-4">
      {/* Captcha Modal */}
      {showCaptchaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCloseModal}
          />
          
          {/* Modal */}
          <div className="relative bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 sm:p-8 max-w-md w-full mx-4 animate-fade-in">
            {/* Close button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-900 dark:text-dark-500 dark:hover:text-white transition-colors"
            >
              <IconX className="w-5 h-5" />
            </button>
            
            {/* Header */}
            <div className="text-center mb-4 sm:mb-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <IconShield className="w-6 h-6 sm:w-8 sm:h-8 text-primary-500" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2">
                Подтверждение
              </h3>
              <p className="text-gray-600 dark:text-dark-400 text-xs sm:text-sm">
                Пожалуйста, пройдите проверку для продолжения регистрации
              </p>
            </div>
            
            {/* Captcha */}
            <div className="flex justify-center">
              <Turnstile
                ref={captchaRef}
                siteKey="0x4AAAAAAEB62HEIG4B-caqj"
                theme="dark"
                onSuccess={handleCaptchaVerify}
                onExpire={() => {
                  setCaptchaToken(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5">
            <LogoIcon />
            <span className="text-xl font-bold text-gray-900 dark:text-white">
              XN Connect
            </span>
          </Link>
        </div>

        {/* Auth Card */}
        <div className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 sm:p-8">
          {/* Tabs */}
          <div className="flex rounded-lg bg-gray-100 dark:bg-dark-950 p-1 mb-6 sm:mb-8">
            <button
              onClick={() => { setIsLogin(true); setError(null); setResendMessage(null); setLoginVerifyEmail(null); }}
              className={`flex-1 py-2 text-sm sm:text-base rounded-md font-medium transition-colors ${
                isLogin 
                  ? 'bg-primary-500 text-white' 
                  : 'text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Вход
            </button>
            <button
              onClick={() => { setIsLogin(false); setError(null); setResendMessage(null); setLoginVerifyEmail(null); }}
              className={`flex-1 py-2 rounded-md font-medium transition-colors ${
                !isLogin 
                  ? 'bg-primary-500 text-white' 
                  : 'text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Регистрация
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6">
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <IconAlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
              {loginVerifyEmail && resendMessage && (
                <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mt-2">
                  <IconAlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <p className="text-blue-400 text-sm">{resendMessage}</p>
                </div>
              )}
              {loginVerifyEmail && !resendMessage && (
                <button
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="mt-2 w-full py-2.5 border border-primary-500/50 text-primary-500 hover:bg-primary-500/10 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {resending ? (
                    <IconLoader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <IconMail className="w-4 h-4" />
                  )}
                  Отправить письмо с подтверждением ещё раз
                </button>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                {isLogin ? 'Имя пользователя или email' : 'Имя пользователя'}
              </label>
              <div className="relative">
                <IconUser className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={isLogin ? 'Введите имя или email' : 'Введите имя'}
                  className="w-full pl-10 pr-4 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                  disabled={loading}
                />
              </div>
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                  Email
                </label>
                <div className="relative">
                  <IconMail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                    disabled={loading}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-dark-500">
                  На указанный email будет отправлено письмо для подтверждения аккаунта
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                Пароль
              </label>
              <div className="relative">
                <IconLock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Введите пароль"
                  className="w-full pl-10 pr-10 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  {showPassword ? <IconEyeOff className="w-5 h-5" /> : <IconEye className="w-5 h-5" />}
                </button>
              </div>
              {!isLogin && (
                <div className="mt-2 space-y-1">
                  <p className={`text-xs flex items-center gap-1 ${password.length >= 8 ? 'text-green-400' : 'text-gray-500 dark:text-dark-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${password.length >= 8 ? 'bg-green-400' : 'bg-gray-300 dark:bg-dark-600'}`}></span>
                    Минимум 8 символов
                  </p>
                  <p className={`text-xs flex items-center gap-1 ${/[A-Z]/.test(password) ? 'text-green-400' : 'text-gray-500 dark:text-dark-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${/[A-Z]/.test(password) ? 'bg-green-400' : 'bg-gray-300 dark:bg-dark-600'}`}></span>
                    Одна заглавная буква
                  </p>
                  <p className={`text-xs flex items-center gap-1 ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? 'text-green-400' : 'text-gray-500 dark:text-dark-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? 'bg-green-400' : 'bg-gray-300 dark:bg-dark-600'}`}></span>
                    Один специальный символ (!@#$%^&*...)
                  </p>
                </div>
              )}
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                  Подтверждение пароля
                </label>
                <div className="relative">
                  <IconLock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Повторите пароль"
                    className="w-full pl-10 pr-4 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {isLogin && (
              <div className="text-right -mt-2">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-500 hover:underline inline-flex items-center gap-1"
                >
                  <IconKey className="w-3.5 h-3.5" />
                  Забыли пароль?
                </Link>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white flex items-center justify-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <IconLoader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <span>{isLogin ? 'Войти' : 'Создать аккаунт'}</span>
                  <IconArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-gray-600 dark:text-dark-500 text-sm mt-6">
            {isLogin ? (
              <>
                Нет аккаунта?{' '}
                <button 
                  onClick={() => { setIsLogin(false); setError(null); setResendMessage(null); setLoginVerifyEmail(null); }} 
                  className="text-primary-500 hover:underline"
                >
                  Зарегистрируйтесь
                </button>
              </>
            ) : (
              <>
                Уже есть аккаунт?{' '}
                <button 
                  onClick={() => { setIsLogin(true); setError(null); setResendMessage(null); setLoginVerifyEmail(null); }} 
                  className="text-primary-500 hover:underline"
                >
                  Войдите
                </button>
              </>
            )}
          </p>
        </div>

        <p className="text-center mt-6">
          <Link to="/" className="text-gray-600 dark:text-dark-500 hover:text-gray-900 dark:hover:text-white transition-colors">
            ← Вернуться на главную
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
